import { PhonebookContact, ContactCategory } from '@openmirror/shared';

/**
 * Contact validation utilities for the enhanced phonebook feature
 */

// Phone number validation and sanitization
export interface PhoneValidationResult {
  isValid: boolean;
  sanitizedNumber: string | null;
  error?: string;
}

/**
 * Sanitizes and validates a phone number according to international E.164 format
 * Supports international formats with or without + prefix
 * Pattern: ^\+?[1-9]\d{1,14}$ (matches backend API validation)
 * @param number - The phone number to sanitize
 * @returns PhoneValidationResult with validation status and sanitized number
 */
export const validateAndSanitizePhoneNumber = (number: string): PhoneValidationResult => {
  if (!number || typeof number !== 'string') {
    return {
      isValid: false,
      sanitizedNumber: null,
      error: 'Phone number is required'
    };
  }

  // Remove all non-digit characters except +
  let sanitized = number.replace(/[^\d+]/g, '');

  // Handle multiple + signs - keep only the first one if it's at the start
  if (sanitized.startsWith('+')) {
    sanitized = '+' + sanitized.substring(1).replace(/\+/g, '');
  } else {
    sanitized = sanitized.replace(/\+/g, '');
  }

  // Remove leading zeros (except for single digit numbers)
  sanitized = sanitized.replace(/^0+/, '');

  // If the number doesn't start with +, add it for international format
  if (!sanitized.startsWith('+')) {
    // Check if it looks like an international number without +
    if (sanitized.length >= 10) {
      sanitized = '+' + sanitized;
    } else {
      // For shorter numbers, add + prefix anyway
      sanitized = '+' + sanitized;
    }
  }

  // Validate using international E.164 pattern (same as backend)
  const internationalPattern = /^\+[1-9]\d{1,14}$/;
  if (internationalPattern.test(sanitized)) {
    return {
      isValid: true,
      sanitizedNumber: sanitized
    };
  }

  return {
    isValid: false,
    sanitizedNumber: null,
    error: 'Invalid phone number format. Phone numbers must be in international format (+country code followed by number)'
  };
};

// Name validation
export interface NameValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Validates a contact name
 * @param name - The name to validate
 * @returns NameValidationResult with validation status
 */
export const validateContactName = (name: string): NameValidationResult => {
  if (!name || typeof name !== 'string') {
    return {
      isValid: false,
      error: 'Name is required'
    };
  }

  const trimmedName = name.trim();
  
  if (trimmedName.length === 0) {
    return {
      isValid: false,
      error: 'Name cannot be empty'
    };
  }

  if (trimmedName.length < 2) {
    return {
      isValid: false,
      error: 'Name must be at least 2 characters long'
    };
  }

  if (trimmedName.length > 100) {
    return {
      isValid: false,
      error: 'Name cannot exceed 100 characters'
    };
  }

  // Check for valid characters (letters, spaces, hyphens, apostrophes, periods)
  const nameRegex = /^[a-zA-Z\s\-'.]+$/;
  if (!nameRegex.test(trimmedName)) {
    return {
      isValid: false,
      error: 'Name can only contain letters, spaces, hyphens, apostrophes, and periods'
    };
  }

  return {
    isValid: true
  };
};

// Category validation
export interface CategoryValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Validates a contact category name
 * @param categoryName - The category name to validate
 * @param existingCategories - Array of existing categories to check for duplicates
 * @param excludeCategoryId - Optional category ID to exclude from duplicate check (for updates)
 * @returns CategoryValidationResult with validation status
 */
export const validateCategoryName = (
  categoryName: string, 
  existingCategories: ContactCategory[] = [],
  excludeCategoryId?: string
): CategoryValidationResult => {
  if (!categoryName || typeof categoryName !== 'string') {
    return {
      isValid: false,
      error: 'Category name is required'
    };
  }

  const trimmedName = categoryName.trim();
  
  if (trimmedName.length === 0) {
    return {
      isValid: false,
      error: 'Category name cannot be empty'
    };
  }

  if (trimmedName.length < 2) {
    return {
      isValid: false,
      error: 'Category name must be at least 2 characters long'
    };
  }

  if (trimmedName.length > 50) {
    return {
      isValid: false,
      error: 'Category name cannot exceed 50 characters'
    };
  }

  // Check for valid characters (letters, numbers, spaces, hyphens, underscores)
  const categoryRegex = /^[a-zA-Z0-9\s\-_]+$/;
  if (!categoryRegex.test(trimmedName)) {
    return {
      isValid: false,
      error: 'Category name can only contain letters, numbers, spaces, hyphens, and underscores'
    };
  }

  // Check for duplicate category names (case-insensitive)
  const isDuplicate = existingCategories.some(category => 
    category.id !== excludeCategoryId && 
    category.name.toLowerCase() === trimmedName.toLowerCase()
  );

  if (isDuplicate) {
    return {
      isValid: false,
      error: 'A category with this name already exists'
    };
  }

  return {
    isValid: true
  };
};

// Complete contact validation
export interface ContactValidationResult {
  isValid: boolean;
  errors: {
    name?: string;
    phone?: string;
    category?: string;
  };
  sanitizedContact?: Partial<PhonebookContact>;
}

/**
 * Validates a complete contact object
 * @param contact - The contact data to validate
 * @param existingCategories - Array of existing categories for category validation
 * @returns ContactValidationResult with validation status and sanitized data
 */
export const validateContact = (
  contact: Partial<PhonebookContact>,
  existingCategories: ContactCategory[] = []
): ContactValidationResult => {
  const errors: { name?: string; phone?: string; category?: string } = {};
  let isValid = true;

  // Validate name
  const nameValidation = validateContactName(contact.name || '');
  if (!nameValidation.isValid) {
    errors.name = nameValidation.error;
    isValid = false;
  }

  // Validate phone number
  const phoneValidation = validateAndSanitizePhoneNumber(contact.number || '');
  if (!phoneValidation.isValid) {
    errors.phone = phoneValidation.error;
    isValid = false;
  }

  // Validate category (if provided)
  if (contact.category) {
    const categoryExists = existingCategories.some(cat => 
      cat.name.toLowerCase() === contact.category!.toLowerCase()
    );
    if (!categoryExists) {
      errors.category = 'Selected category does not exist';
      isValid = false;
    }
  }

  const sanitizedContact: Partial<PhonebookContact> = {
    ...contact,
    name: contact.name?.trim(),
    number: phoneValidation.sanitizedNumber || contact.number,
    category: contact.category || 'General'
  };

  return {
    isValid,
    errors,
    sanitizedContact: isValid ? sanitizedContact : undefined
  };
};

// Duplicate detection
export interface DuplicateDetectionResult {
  isDuplicate: boolean;
  existingContact?: PhonebookContact;
  duplicateType: 'phone' | 'none';
}

/**
 * Detects if a contact is a duplicate based on phone number
 * @param newContact - The new contact to check
 * @param existingContacts - Array of existing contacts
 * @param excludeContactId - Optional contact ID to exclude from duplicate check (for updates)
 * @returns DuplicateDetectionResult with duplicate status and existing contact info
 */
export const detectDuplicateContact = (
  newContact: Partial<PhonebookContact>,
  existingContacts: PhonebookContact[],
  excludeContactId?: string
): DuplicateDetectionResult => {
  if (!newContact.number) {
    return {
      isDuplicate: false,
      duplicateType: 'none'
    };
  }

  // Sanitize the new contact's phone number for comparison
  const phoneValidation = validateAndSanitizePhoneNumber(newContact.number);
  if (!phoneValidation.isValid || !phoneValidation.sanitizedNumber) {
    return {
      isDuplicate: false,
      duplicateType: 'none'
    };
  }

  // Check for duplicate phone numbers
  const duplicateContact = existingContacts.find(contact => 
    contact.id !== excludeContactId && 
    contact.number === phoneValidation.sanitizedNumber
  );

  if (duplicateContact) {
    return {
      isDuplicate: true,
      existingContact: duplicateContact,
      duplicateType: 'phone'
    };
  }

  return {
    isDuplicate: false,
    duplicateType: 'none'
  };
};

// Batch validation for imports
export interface BatchValidationResult {
  validContacts: PhonebookContact[];
  invalidContacts: Array<{
    contact: Partial<PhonebookContact>;
    errors: { name?: string; phone?: string; category?: string };
    originalRowIndex: number;
  }>;
  duplicates: Array<{
    contact: Partial<PhonebookContact>;
    existingContact: PhonebookContact;
    originalRowIndex: number;
  }>;
}

/**
 * Validates a batch of contacts for import operations
 * @param contacts - Array of contacts to validate with their original row indices
 * @param existingContacts - Array of existing contacts for duplicate detection
 * @param existingCategories - Array of existing categories for category validation
 * @returns BatchValidationResult with categorized validation results
 */
export const validateContactBatch = (
  contacts: Array<{ contact: Partial<PhonebookContact>; originalRowIndex: number }>,
  existingContacts: PhonebookContact[],
  existingCategories: ContactCategory[]
): BatchValidationResult => {
  const validContacts: PhonebookContact[] = [];
  const invalidContacts: Array<{
    contact: Partial<PhonebookContact>;
    errors: { name?: string; phone?: string; category?: string };
    originalRowIndex: number;
  }> = [];
  const duplicates: Array<{
    contact: Partial<PhonebookContact>;
    existingContact: PhonebookContact;
    originalRowIndex: number;
  }> = [];

  contacts.forEach(({ contact, originalRowIndex }) => {
    // Validate the contact
    const validation = validateContact(contact, existingCategories);
    
    if (!validation.isValid) {
      invalidContacts.push({
        contact,
        errors: validation.errors,
        originalRowIndex
      });
      return;
    }

    // Check for duplicates
    const duplicateCheck = detectDuplicateContact(contact, existingContacts);
    if (duplicateCheck.isDuplicate && duplicateCheck.existingContact) {
      duplicates.push({
        contact,
        existingContact: duplicateCheck.existingContact,
        originalRowIndex
      });
      return;
    }

    // If valid and not duplicate, add to valid contacts
    if (validation.sanitizedContact) {
      validContacts.push({
        id: crypto.randomUUID(),
        name: validation.sanitizedContact.name!,
        number: validation.sanitizedContact.number!,
        category: validation.sanitizedContact.category!,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    }
  });

  return {
    validContacts,
    invalidContacts,
    duplicates
  };
};

// Legacy function for backward compatibility
/**
 * Legacy phone number sanitization function for backward compatibility
 * Now supports international numbers (same as validateAndSanitizePhoneNumber)
 * @deprecated Use validateAndSanitizePhoneNumber instead
 * @param number - The phone number to sanitize
 * @returns Sanitized phone number or null if invalid
 */
export const sanitizePhoneNumber = (number: string): string | null => {
  const result = validateAndSanitizePhoneNumber(number);
  return result.sanitizedNumber;
};